import { Button, Card } from "@rumbledirect/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Input, message, Modal, Radio, Typography } from "antd";
import { useState } from "react";
import { MIME_TYPE_MERGE_PATCH_JSON } from "@rumble-it/common-lib/src/constants/mimeTypes";
import { useViewConfigurationClient } from "../client";

export interface PickupServiceCardProps {
  title: string;
  description: string;
  emptyState: string;
  submitEndpoint: string;
  submitField: string;
  addressNameField: string;
  addressField: string;
  value: boolean;
  selectedName: string;
  selectedAddress: string;
}

interface CustomAddress {
  name: string;
  street: string;
  streetNumber: string;
  streetNumberAddition: string;
  postalCode: string;
  city: string;
}

const emptyAddress: CustomAddress = {
  name: "",
  street: "",
  streetNumber: "",
  streetNumberAddition: "",
  postalCode: "",
  city: "",
};

export const PickupServiceCard = ({
  title,
  description,
  emptyState,
  submitEndpoint,
  submitField,
  addressNameField,
  addressField,
  value,
  selectedName,
  selectedAddress,
}: PickupServiceCardProps) => {
  const axiosInstance = useViewConfigurationClient();
  const queryClient = useQueryClient();
  const [optionsVisible, setOptionsVisible] = useState(false);
  const [enabled, setEnabled] = useState(value);
  const [address, setAddress] = useState<CustomAddress>(emptyAddress);

  const openModal = () => {
    setEnabled(value);
    setAddress(emptyAddress);
    setOptionsVisible(true);
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      const hasCustomAddress = Object.values(address).some(
        (fieldValue) => fieldValue.trim() !== "",
      );

      return axiosInstance.patch(
        submitEndpoint,
        {
          [submitField]: enabled,
          [addressNameField]:
            enabled && address.name.trim() ? address.name.trim() : null,
          [addressField]:
            enabled && hasCustomAddress
              ? {
                  street: address.street || null,
                  streetNumber: address.streetNumber || null,
                  streetNumberAddition: address.streetNumberAddition || null,
                  postalCode: address.postalCode || null,
                  city: address.city || null,
                  country: "NL",
                }
              : null,
        },
        { headers: { "Content-Type": MIME_TYPE_MERGE_PATCH_JSON } },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["view-configuration"] });
      setOptionsVisible(false);
    },
    onError: () => {
      message.error("Er is een fout opgetreden. Probeer het opnieuw.");
    },
  });

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{title}</span>
          <Button
            type="link"
            style={{ padding: 0, fontWeight: "normal" }}
            onClick={openModal}
          >
            wijzigen
          </Button>
        </div>
      }
    >
      {value ? (
        <div>
          <Typography.Text>
            {selectedName || "Er is haal- en brengservice geselecteerd"}
          </Typography.Text>
          {selectedAddress && (
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              {selectedAddress}
            </Typography.Paragraph>
          )}
        </div>
      ) : (
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          {emptyState}
        </Typography.Paragraph>
      )}

      <Modal
        title={`${title} wijzigen`}
        open={optionsVisible}
        onCancel={() => setOptionsVisible(false)}
        onOk={() => submitMutation.mutate()}
        okText="Opslaan"
        okButtonProps={{ loading: submitMutation.isPending }}
      >
        <Typography.Paragraph type="secondary">
          {description}
        </Typography.Paragraph>
        <Radio.Group
          value={enabled}
          onChange={(event) => setEnabled(event.target.value)}
          style={{ marginBottom: 16 }}
        >
          <Radio value={true}>Ja</Radio>
          <Radio value={false}>Nee</Radio>
        </Radio.Group>

        {enabled && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <Typography.Text strong>
              Afwijkende ophaallocatie (optioneel)
            </Typography.Text>
            <Input
              placeholder="Naam"
              value={address.name}
              onChange={(event) =>
                setAddress({ ...address, name: event.target.value })
              }
            />
            <Input
              placeholder="Straat"
              value={address.street}
              onChange={(event) =>
                setAddress({ ...address, street: event.target.value })
              }
            />
            <div style={{ display: "flex", gap: 12 }}>
              <Input
                placeholder="Huisnummer"
                value={address.streetNumber}
                onChange={(event) =>
                  setAddress({ ...address, streetNumber: event.target.value })
                }
              />
              <Input
                placeholder="Toevoeging"
                value={address.streetNumberAddition}
                onChange={(event) =>
                  setAddress({
                    ...address,
                    streetNumberAddition: event.target.value,
                  })
                }
              />
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <Input
                placeholder="Postcode"
                value={address.postalCode}
                onChange={(event) =>
                  setAddress({ ...address, postalCode: event.target.value })
                }
              />
              <Input
                placeholder="Stad"
                value={address.city}
                onChange={(event) =>
                  setAddress({ ...address, city: event.target.value })
                }
              />
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
};
